import {
  IAdServerTargeting,
  IBidResponse,
  IPrebidAdUnit,
  IBidRequest
} from '../../typings/IPrebid'
import GoogletagFacade, {
  OUTSTREAM_LINE_ITEM_VERSION
} from '../googletagFacade/GoogletagFacade'
import { deepcopy } from '../helpers/deepcopy'
import Logger from '../helpers/Logger'
import { spread } from '../helpers/spread'
import WebModel from '../models/WebModel'
import * as Slots from './'
import SlotCounter from './SlotCounter'
import { Device } from '../helpers'
import { SLOT_RENDER_ATTR, OUTSTREAM_FLOOR } from '../constants'
import { addClass, addAdDimensionsClass, removeClass } from '../helpers/DOM'
import Lazyable from '../lazyable/Lazyable'
import { AdType, IAdType } from '../enums/AdType'
import { MediaType } from '../enums/MediaType'
import { calculateUrTargeting } from '../googletagFacade/helpers/targetingCalculators'

// tslint:disable no-magic-numbers
// Note: This sizes below are ordered by their revenue, descending (except 1x1)
// https://github.com/mediavine/web-wrapper/issues/774
export const validPrebidSizes: Array<AdSize> = [
  [300, 250],
  [728, 90],
  [300, 600],
  [160, 600],
  [320, 50],
  [300, 50],
  [320, 100],
  [250, 250],
  [468, 60],
  [336, 280],
  [970, 250],
  [970, 90],
  [300, 1050]
]
// tslint:enable no-magic-numbers

export abstract class Slot {
  private _wrapper: HTMLElement
  private _targeting: ISlotTargeting = {}
  private _gSlot: googletag.Slot
  protected _sizes: AdSizes
  number: number
  adUnitId: string
  refreshes = 0
  type: SlotClass
  lazy: boolean
  lazyable: Lazyable
  sticky?: boolean
  stickyOffset?: number
  stickyStopSelector?: string
  selector: string | HTMLElement
  slotName: string
  insertPosition: InsertPosition
  refreshRetries: number = 0
  refreshTime?: number
  /** pixels from bottom of screen where lazy loading add will render */
  offset: number
  dynamic: boolean
  timeout?: number
  highestPrebid?: IBidResponse | null
  target: HTMLElement | null
  /** Height of ad within slot */
  height: number
  /** Width of ad within slot */
  width: number
  adType: IAdType
  /**
   * A observer is assigned for refreshable ads.
   * If the slot or its subtree is modified,
   * we resize our containers/iframes.
   */
  resizeObserver?: MutationObserver

  constructor(
    readonly slotModel: ISlotModel,
    readonly model: WebModel,
    protected _document: Document = document
  ) {
    spread(slotModel).onto(this)
    this.number = SlotCounter.current(slotModel.type)
    this.target = getTarget(slotModel)
    this.sticky = this.getSticky()
    this.stickyOffset = this.getStickyOffset()
    this.stickyStopSelector = this.getStickyStopSelector()
  }

  get sizes(): AdSizes {
    return this.checkSizes(this._sizes)
  }

  set sizes(updatedAdSizes: AdSizes) {
    this._sizes = updatedAdSizes
  }

  private checkSizes(sizes: AdSizes): AdSizes {
    const minAdSize = 320
    const element =
      typeof this.selector === 'string'
        ? document.querySelector(this.selector)
        : (this.selector as HTMLElement)
    const parent = element && (element.parentNode as HTMLElement | null)
    const parentWidth = (parent && parent.offsetWidth) || Infinity
    return sizes.filter(
      (size) =>
        !Array.isArray(size) || size[0] <= minAdSize || size[0] <= parentWidth
    )
  }

  validate(): boolean {
    return !this.hasRendered()
  }

  hasRendered(): boolean {
    return (
      !!this.target &&
      this.target.hasAttribute(`${SLOT_RENDER_ATTR}-${this.slotModel.adUnitId}`)
    )
  }

  get bidRequests(): Array<IBidRequest> {
    const unit = this.model.bidRequests[this.adUnitId]
    return deepcopy(unit)
  }

  get id(): string {
    if (this.number === 1) {
      return this.adUnitId
    } else {
      return this.adUnitId.replace('_', `_${this.number}_`)
    }
  }

  /**
   * Previously treated as an optional abstract.
   * Treat this method as final unless you can not
   * get your desired result from overriding the adUnit
   * method.
   */
  adunits(): Array<IPrebidAdUnit> {
    return [
      {
        code: this.id,
        bids: this.bidRequests,
        mediaTypes: {
          banner: {
            sizes: Slot.getValidPrebidSizes(this.sizes)
          }
        }
      }
    ]
  }

  get gSlot(): googletag.Slot {
    if (!this._gSlot) {
      this._gSlot = GoogletagFacade.registerSlot(this)
    }

    return this._gSlot
  }

  get wrapperId(): string {
    // Use while A/B testing 1.0 to 2.0
    return `${this.id}_wrapper`

    // Use after 2.0 launch
    // return `${this.adUnitId}_wrapper`
  }

  get wrapper(): HTMLElement {
    if (this._wrapper && this._wrapper.id === this.wrapperId) {
      return this._wrapper
    }

    const wrapper = document.querySelector(
      `[data-wrapper="${this.id}"]`
    ) as HTMLElement
    this._wrapper = wrapper
    return this._wrapper
  }

  get isRefreshing(): boolean {
    return !!this.refreshTime && this.refreshTime > 0 && this.refreshes >= 1
  }

  destroyGSlot() {
    window.googletag.destroySlots([this._gSlot])
    delete this._gSlot
  }

  // Safe to override
  template(): string {
    return `
      <div id='${this.wrapperId}' class='adunitwrapper ${this.adUnitId}_wrapper mv-empty-wrapper' data-wrapper='${this.id}'>
        <div id='${this.id}' class='adunit'>
        </div>
      </div>
    `
  }

  setTargeting(targetingValues: Partial<ISlotTargeting>): Slot {
    this._targeting = {
      ...this._targeting,
      ...targetingValues
    }

    return this
  }

  applyTargeting(): Slot {
    if (!this.gSlot) {
      Logger.debug(`gSlot is undefined on ${this.id}`)
    }

    this.gSlot.clearTargeting()
    this.filterTargeting()
    Object.keys(this._targeting).forEach((key) => {
      const value = this._targeting[key]
      if (value || value === 0) {
        this.gSlot.setTargeting(key, value)
      }
    })

    // Clear targeting so weird stuff doesn't happen with refreshables
    this._targeting = {}

    return this
  }

  private filterTargeting(): void {
    // set UR if no bids
    if (!this._targeting.hb_pb) {
      this._targeting.UR = calculateUrTargeting(
        this.model.floor[MediaType.banner]
      )
    }

    // highest bid is below the floor.
    if (
      isBelowFloor(this._targeting, this.model.floor[MediaType.banner]) ||
      isBelowOutstreamFloor(
        this._targeting,
        this.model.floor[MediaType.outstream]
      )
    ) {
      this._targeting.hb_adid = undefined
      this._targeting.hb_bidder = undefined
      this._targeting.hb_pb = undefined
      this._targeting.UR = calculateUrTargeting(
        this.model.floor[MediaType.banner]
      )
    }

    /* Ceiling velocity to 1000 */
    if (this._targeting.velocity && this._targeting.velocity > 1000) {
      this._targeting.velocity = 1000
    }
  }

  private getSticky(): boolean {
    const attr = 'data-sticky-slot'
    return (
      this.slotModel.sticky ||
      (!!this.target && this.target.hasAttribute(`${attr}`))
    )
  }

  private getStickyOffset(): number | undefined {
    const attr = 'data-sticky-slot-offset'
    let val = this.slotModel.stickyOffset
    if (this.target && this.target.hasAttribute(attr)) {
      val = parseInt(this.target.getAttribute(attr) || '', 10)
    }

    return val
  }

  private getStickyStopSelector(): string | undefined {
    const attr = 'data-sticky-slot-stop'
    let val = this.slotModel.stickyStopSelector
    if (this.target && this.target.hasAttribute(attr)) {
      val = this.target.getAttribute(attr) || ''
    }

    return val
  }

  static getValidPrebidSizes(sizes: AdSizes): AdSizes {
    return sizes.filter((size) => {
      const isValidSize = validPrebidSizes.find(
        (validSize) => size[0] === validSize[0] && size[1] === validSize[1]
      )

      return size !== 'fluid' && isValidSize
    })
  }

  get iframe(): HTMLIFrameElement {
    return (
      this.wrapper.querySelector('.adunit iframe') ||
      document.createElement('iframe')
    )
  }

  sizeContainers({ height, width, adType, wrapper } = this): void {
    this.resetSizeClasses()
    addAdDimensionsClass({ size: [width, height] }, wrapper)
    this.displayAdUnitLabels(adType)
    switch (adType) {
      case AdType.prebidStandard:
        this.sizeWrapper()
        this.sizeIframe()
        addClass(wrapper, 'mv-dynamic-size')
        break

      case AdType.adXStandard:
        this.sizeWrapper()
        break

      case AdType.prebidNative:
        wrapper.style.minHeight = `initial`
        wrapper.style.width = `initial`
        addClass(wrapper, 'mv-native-size')
        break

      case AdType.adXNative:
        this.sizeAdxNativeWrapper()
        addClass(this.wrapper, 'native')
        break
    }
  }

  resetSizeClasses({ wrapper } = this) {
    removeClass(wrapper, 'mv-dynamic-size')
    removeClass(wrapper, 'native')
    removeClass(wrapper, 'mv-empty-wrapper')
    removeClass(wrapper, 'mv-native-size')
  }

  sizeIframe({ iframe, height, width } = this) {
    iframe.style.height = `${height}px`
    iframe.style.width = `${width}px`
  }

  sizeWrapper({ wrapper, height, width } = this) {
    const logoBuffer = 14
    wrapper.style.minHeight = `${height + logoBuffer}px`
    wrapper.style.width = `${width}px`
    wrapper.style.display = 'block'
  }

  /**
   * For Adx Native ads, a Mutation Observer is used to listen for
   * changes to the height attribute within the wrapper's iframe.
   * This allows the parent wrapper to receive a 'min-height'
   * attribute that is based upon the size of the child iframe. This is
   * done to prevent "content skipping/shifting" issues when Lazyable
   * ad cleanup occurs, resolving the issue found in
   * https://github.com/mediavine/web-wrapper/issues/924
   */
  sizeAdxNativeWrapper({ wrapper } = this) {
    const logoBuffer = 14
    const iframe = wrapper.querySelector('iframe')

    if (iframe) {
      const mutationCallback = (
        mutationsList: Array<MutationRecord>,
        observer: MutationObserver
      ) => {
        mutationsList.forEach(({ attributeName }) => {
          if (attributeName === 'height') {
            wrapper.style.minHeight = `${iframe.offsetHeight + logoBuffer}px`
            observer.disconnect()
          }
        })
      }
      const observer = new MutationObserver(mutationCallback)
      observer.observe(iframe, { attributes: true })
    }
  }

  displayAdUnitLabels(adType: IAdType) {
    const labels = this.wrapper.querySelector('.adunitlabel') as HTMLElement
    if (!labels) {
      return
    }
    labels.style.display = 'block'
    if (adType === AdType.prebidNative) {
      labels.style.width = `100%`
    } else {
      labels.style.width = 'inherit'
    }
  }
}

declare global {
  type SlotClass = keyof typeof Slots
}

export interface ISlotTargeting extends Partial<IAdServerTargeting> {
  amznbid?: string
  amzniid?: string
  amznsize?: string
  bidFloor?: string
  dynFloor?: string
  /** used to compare bucketed hb_pb vs the unbucketed cpm  */
  hb_bid?: string
  hb_bidder?: string
  hb_count?: string
  hb_pmp?: string
  hb_pool?: string
  ad_size?: string
  lazy?: '1'
  maxVelocity?: number
  OE?: '1' | '0'
  offsetMultiplier?: number
  pid?: string
  player_group?: string
  psa?: Array<string>
  pp?: Array<string>
  refresh_count?: number
  refresh?: string
  slot_number?: string
  slot?: string
  slot_id?: string
  timeout?: number
  UR?: string
  URP?: string
  velocity?: number
  sizeLift?: string
  hb_pb_amazon?: string | number
  hb_pb_aol?: string | number
  hb_pb_appnexus?: string | number
  hb_pb_appnexusAst?: string | number
  hb_pb_indexExchange?: string | number
  hb_pb_gumgum?: string | number
  hb_pb_rubicon?: string | number
  hb_pb_triplelift?: string | number
  hb_liv?: string
  hb_safeframe?: '1' | '0'
  ccpa?: string
}

function getTarget({ selector, mobileSelector }: ISlotModel): HTMLElement {
  if (selector instanceof Element) {
    return selector as HTMLElement
  } else {
    return Device.isMobileOrTablet && mobileSelector
      ? (document.querySelector(mobileSelector) as HTMLElement)
      : (document.querySelector(selector) as HTMLElement)
  }
}

export function isBelowOutstreamFloor(
  { hb_pb, hb_bidder, hb_liv }: ISlotTargeting,
  floor: number = OUTSTREAM_FLOOR
): boolean {
  // Check if this is even an outstream bid
  if (!hb_bidder || hb_liv !== OUTSTREAM_LINE_ITEM_VERSION) {
    return false
  }

  return isBelowFloor({ hb_pb }, floor)
}

export function isBelowFloor(
  { hb_pb }: ISlotTargeting,
  floor: number
): boolean {
  if (!hb_pb) {
    return false
  }

  return parseFloat(hb_pb) < floor
}
